/**
 * Product Configuration System
 * Detects product based on hostname (no .env needed)
 *
 * All configs live in prd/core and are merged to both branches
 */

import { meetingsConfig } from "./products/meetings.config";
import { interviewConfig } from "./products/interview.config";
import { recruitersConfig } from "./products/recruiters.config";
import { englishConfig } from "./products/english.config";
import { frenchConfig } from "./products/french.config";

export type ProductType = "meetings" | "interview" | "recruiters" | "english" | "french";

export interface ProductConfig {
  // Identity
  id: ProductType;
  name: string;
  displayName: string;

  // API
  collectionType: "meeting" | "interview" | "recruiters" | "english" | "french";

  // Theming
  theme: ThemeConfig;

  // Static texts/labels
  i18n: I18nConfig;

  // Assets (prepared for future use)
  assets: AssetsConfig;

  // Support
  support: SupportConfig;
}

interface SupportConfig {
  whatsapp: {
    enabled: boolean;
    phoneNumber: string; // Format: country code + number (e.g., "351921459199")
    defaultMessage: string;
  };
  email: string;
}

interface ThemeConfig {
  primary: string; // HSL format: "225 75% 35%"
  accent: string; // HSL format: "270 65% 50%"
}

interface I18nConfig {
  // App branding
  appName: string;
  appTagline: string;

  // Navigation & Common labels
  dashboard: string;
  profile: string;
  settings: string;
  logout: string;
  backToHome: string;

  // Auth pages
  loginTitle: string;
  loginSubtitle: string;
  signupTitle: string;
  signupSubtitle: string;
  signInToContinue: string;
  welcomeBack: string;

  // Meeting/Interview pages
  preparationTitle: string;
  simulationTitle: string;
  feedbackTitle: string;
  startPreparation: string;
  startSimulation: string;
  continueSimulation: string;

  // Paywall & Premium
  unlockPremiumFeatures: string;
  upgradeToPremium: string;
  premiumFeatures: string;
  premiumDescription: string;
  goToDashboard: string;
  viewProfile: string;

  // Actions
  start: string;
  continue: string;
  finish: string;
  cancel: string;
  save: string;
  loading: string;
  tryAgain: string;

  // Success messages
  welcomeMessage: string;
  welcomeToPremium: string;
  paymentSuccessful: string;
  subscriptionActivated: string;

  // Error messages
  genericError: string;
  networkError: string;
  failedToLoadCategories: string;
  noConversationCategories: string;

  // Profile page
  profilePhoto: string;
  basicInformation: string;
  subscriptionStatus: string;

  // Loading states
  loadingSubscription: string;

  // Landing page sections
  hero: {
    title: string;
    subtitle: string;
    description: string;
    ctaButton: string;
  };

  categories: {
    badge: string;
    title: string;
    subtitle: string;
    subtitleDynamic: (count: number) => string;
    subtitleFallback: string;
  };

  testimonials: {
    badge: string;
    title: string;
    subtitle: string;
    testimonial1: {
      quote: string;
      name: string;
      role: string;
      company: string;
    };
    testimonial2: {
      quote: string;
      name: string;
      role: string;
      company: string;
    };
    testimonial3: {
      quote: string;
      name: string;
      role: string;
      company: string;
    };
  };

  features?: {
    badge: string;
    title: string;
    subtitle: string;
    realistic: {
      title: string;
      description: string;
    };
    feedback: {
      title: string;
      description: string;
    };
    practice: {
      title: string;
      description: string;
    };
    track: {
      title: string;
      description: string;
    };
    privacy: {
      title: string;
      description: string;
    };
    storage: {
      title: string;
      description: string;
    };
    bottomMessage: string;
  };

  dataProtection?: {
    badge: string;
    title: string;
    subtitle: string;
    encryption: {
      title: string;
      description: string;
    };
    privacy: {
      title: string;
      description: string;
    };
    storage: {
      title: string;
      description: string;
    };
    bottomMessage: string;
  };

  howItWorks: {
    badge: string;
    title: string;
    subtitle: string;
    step1: {
      title: string;
      description: string;
    };
    step2: {
      title: string;
      description: string;
    };
    step3: {
      title: string;
      description: string;
    };
  };

  cta: {
    title: string;
    subtitle: string;
    button: string;
  };

  footer: {
    companyName: string;
    companyDescription: string;
    resources: string;
    faq: string;
    privacyPolicy: string;
    termsOfService: string;
    subscriptionBilling: string;
    contact: string;
    contactDescription: string;
    fundingAlt: string;
    copyright: (year: number) => string;
    madeWith: string;
  };

  // Dashboard page
  dashboardPage: {
    welcome: string;
    subtitle: string;
    totalSimulations: string;
    averageScore: string;
    lastPractice: string;
    ctaEmoji: string;
    ctaTitle: string;
    ctaSubtitle: string;
    ctaDescription: string;
    ctaButton: string;
    recentSessions: string;
    emptySessions: string;
    emptySessionsDescription: string;
    startFirstSession: string;
    topExpressions: string;
    viewFeedback: string;
    score: string;
    tryAgainButton: string;
    refreshButton: string;
  };

  // Profile page
  profilePage: {
    loadingMessage: string;
    photoMaxSize: string;
    invalidFileType: string;
    fileTooLarge: string;
    photoUpdated: string;
    uploadFailed: string;
    updateFailed: string;
    namePlaceholder: string;
    emailPlaceholder: string;
    subscriptionUnavailable: string;
    subscriptionUnavailableDesc: string;
    profileDataNotLoaded: string;
    success: string;
    profileUpdated: string;
    pageTitle: string;
    pageSubtitle: string;
    profilePhotoTitle: string;
    clickToUpload: string;
    basicInformationTitle: string;
    fullName: string;
    emailAddress: string;
    subscriptionManagementTitle: string;
    premiumActive: string;
    manageSubscriptionDesc: string;
    loading: string;
    manageYourSubscription: string;
    unlockPremiumDesc: string;
    getPremium: string;
    cancel: string;
    saving: string;
    saveChanges: string;
    uploadingProgress: (percent: number) => string;
  };

  // Meeting pages
  meetingSimulator: {
    heroTitle: string;
    heroSubtitle: string;
    heroSubtitleWithCount: (count: number) => string;
    searchPlaceholder: string;
    loadingMeetings: string;
    noMeetingsFound: (query: string) => string;
    resources: string;
  };

  meetingCover: {
    loadingDetails: string;
    notFound: string;
    backToAll: string;
    categoryBadge: (category: string) => string;
    prepareFirstTitle: string;
    prepareFirstDescription: string;
    prepareButtonLabel: string;
    startSimulationTitle: string;
    startSimulationDescription: string;
    startSimulationButtonLabel: string;
    backToAllConversations: string;
    prepareYourself: string;
  };

  meetingPreparation: {
    answerPlaceholder: string;
  };

  meetingPreparationSummary: {
    loadingDetails: string;
    notFound: string;
    noData: string;
    incomplete: string;
    configError: string;
    submitSuccess: string;
    submitFailed: string;
    tryExpressionsAgain: string;
    practiceExpressions: string;
    submitting: string;
    submittingDesc: string;
    generatingExpressions: string;
    generatingExpressionsDesc: string;
    attemptOf: (current: number, total: number) => string;
    loadingAudio: string;
    loadingAudioDesc: (count: number) => string;
    percentComplete: (percent: number) => string;
    submissionFailedTitle: string;
    unexpectedError: string;
    tryAgain: string;
    backToPreparation: string;
    greatWork: string;
    yourExpressions: string;
    expressionsDesc: string;
    expression: string;
    viewAll: (count: number) => string;
    whatNext: string;
    practiceExpressionsDesc: string;
    startSimulationDesc: string;
    startSimulation: string;
  };

  meetingSimulation: {
    sessionError: string;
    sessionNotFound: string;
    submissionFailed: string;
    hostSpeaking: string;
    conversationCompleted: string;
    clickToSpeak: string;
    thankYou: string;
    responsesAnalyzed: string;
  };

  // Interview pages
  interviewCompleted: {
    title: string;
    analyzing: string;
    complete: string;
    description: string;
    stepFlow: string;
    stepSkills: string;
    stepFeedback: string;
    stepReport: string;
    sessionError: string;
    sessionNotFound: string;
    processingError: string;
    processingTakingLonger: string;
    processingTakingLongerDesc: string;
    noAssessmentFound: string;
    noAssessmentFoundDesc: string;
    backToDashboard: string;
    analysisFailed: string;
    analysisFailedDesc: string;
  };

  interviewFeedback: {
    title: string;
    invalidAssessment: string;
    loadFailed: string;
    shareTitle: string;
    shareSuccess: string;
    linkCopied: string;
    generatingPdf: string;
    pdfDownloaded: string;
    pdfSuccess: string;
    goToDashboard: string;
    pageTitle: string;
  };

  // Practice Expressions
  practiceExpressions: {
    loadingExpressions: string;
    title: string;
    subtitle: string;
    noExpressionsTitle: string;
    noExpressionsDescription: string;
    goToPreparation: string;
    backToDashboard: string;
    backToSummary: string;
    continueToSimulation: string;
    failedToLoad: string;
    tryAgainFromDashboard: string;
  };

  // Checkout pages
  checkoutSuccess: {
    processing: string;
    processingMessage: string;
    approved: string;
    activatedMessage: string;
    welcomeToPremium: string;
    benefit1: string;
    benefit2: string;
    benefit3: string;
    benefit4: string;
    goToDashboard: string;
    manageSubscription: string;
    noSessionId: string;
    notAuthenticated: string;
    errorProcessing: string;
  };

  checkoutCancelled: {
    title: string;
    message: string;
    whyTitle: string;
    reason1: string;
    reason2: string;
    reason3: string;
    reason4: string;
    backToDashboard: string;
    exploreFree: string;
    needHelp: string;
    supportEmail: string;
  };

  // Not Found page
  notFoundPage: {
    title: string;
    message: string;
  };

  // Components
  paywallComponent: {
    loadingOptions: string;
    loadProductsFailed: string;
    checkoutFailed: string;
  };

  loadingComponent: {
    defaultMessage: string;
    loadingResources: string;
  };

  assessmentComponent: {
    pronunciationError: string;
  };

  assessmentResult: {
    finishPractice: string;
    continueNext: string;
  };
}

interface AssetsConfig {
  logo?: string; // Path to logo (future)
  favicon?: string; // Path to favicon (future)
}

/**
 * Detects product based on hostname
 *
 * Detection rules:
 * - recruiters.globalcoders.app → Recruiters
 * - recruiters.localhost → Recruiters
 * - interview.globalcoders.app → Interview
 * - interview.localhost → Interview
 * - globalcoders.app → Meetings
 * - preview--speakglobalcoders.lovable.app → Meetings
 * - localhost → Meetings (default)
 */
export const detectProduct = (): ProductType => {
  const hostname = window.location.hostname;

  // Recruiters product detection (must check first)
  // Matches: recruiters.globalcoders.app, recruiters.localhost
  if (hostname.includes("recruiters.")) {
    return "recruiters";
  }

  // Interview product detection
  // Matches: interview.globalcoders.app, interview.localhost
  if (hostname.includes("interview.")) {
    return "interview";
  }

  // English product detection
  // Matches: english.globalcoders.app, english.localhost
  if (hostname.includes("english.")) {
    return "english";
  }

  // French product detection
  // Matches: french.globalcoders.app, french.localhost
  if (hostname.includes("french.")) {
    return "french";
  }

  // Meetings product (default)
  // Matches: globalcoders.app, preview--speakglobalcoders.lovable.app, localhost
  return "meetings";
};

/**
 * Get current product configuration
 * Cache is invalidated if hostname changes
 */
let cachedConfig: ProductConfig | null = null;
let cachedHostname: string | null = null;

export const getProductConfig = (): ProductConfig => {
  const currentHostname = window.location.hostname;

  // Revalida cache se hostname mudou ou não existe
  if (cachedConfig && cachedHostname === currentHostname) {
    return cachedConfig;
  }

  const productType = detectProduct();

  const configs = {
    meetings: meetingsConfig,
    interview: interviewConfig,
    recruiters: recruitersConfig,
    english: englishConfig,
    french: frenchConfig,
  };

  cachedConfig = configs[productType];
  cachedHostname = currentHostname;

  return cachedConfig;
};

/**
 * Reset cache (useful for tests)
 * Call this before each test that mocks window.location.hostname
 */
export const resetProductConfig = (): void => {
  cachedConfig = null;
  cachedHostname = null;
};
